import React from 'react'
import { connect } from 'react-redux'
import formatPhoneUS from './helpers/formatPhoneUS'
import Spinner from './Spinner'
import { buildPages } from './helpers/buildPaginationPages'

const emptyList = {
    items: [],
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 0,
    totalItems: 0,
    searchTerm: '',
    loading: false,
};

const csrfToken = () => document.querySelector('meta[name="csrf-token"]').getAttribute('content');

const displayPhone = (phone) => {
    if (!phone) return '-';
    return phone.startsWith('+1') ? formatPhoneUS(phone) : phone;
};

const allowDigitsOnly = (event) => {
    if (
        !/[0-9]/.test(event.key) &&
        !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Enter'].includes(event.key) &&
        !event.ctrlKey && !event.metaKey
    ) {
        event.preventDefault();
    }
};

// Keeps the 10 national digits of a US number, so a pasted "+1 (267) 555-2808" survives intact.
const usPhoneDigits = (value) => {
    const digits = value.replace(/\D/g, '');
    return (digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

class AutoPilotUnsubscribe extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            blocked: { ...emptyList },
            blockPhone: '',
            blockNote: '',
            showSpinner: false,
            spinnerState: 'sync',
            spinnerText: '',
            business: this.props.state.business,
            businessGroupName: window.businessGroupName || null,
        };

        this.searchTimeouts = {};
    }

    endpoint() {
        return `/business/${this.state.business.business_id}/autopilot/blocked-numbers`;
    }

    async fetchList(list, overrides = {}) {
        const current = { ...this.state[list], ...overrides };
        this.setState({ [list]: { ...current, loading: true } });

        try {
            const params = new URLSearchParams({ per_page: current.itemsPerPage, page: current.currentPage });
            const search = current.searchTerm.replace(/\D/g, '');
            if (search) {
                params.set('search', search);
            }

            const response = await fetch(`${this.endpoint()}?${params}`, {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                throw new Error(`Failed to load ${list} list (HTTP ${response.status})`);
            }

            const r = await response.json();
            // Removing the last row on the last page leaves us past the end; step back to the real last page.
            if (r.data.length === 0 && r.meta.current_page > Math.max(1, r.meta.last_page)) {
                return this.fetchList(list, { currentPage: Math.max(1, r.meta.last_page) });
            }

            this.setState({
                [list]: {
                    ...current,
                    items: r.data,
                    currentPage: r.meta.current_page,
                    itemsPerPage: r.meta.per_page,
                    totalPages: r.meta.last_page,
                    totalItems: r.meta.total,
                    loading: false,
                },
            });
        } catch (error) {
            console.error(error);
            this.setState({ [list]: { ...current, loading: false } });
            this.stopSpinner(__('Error loading the list...'), 'error');
        }
    }

    componentDidMount() {
        this.fetchList('blocked');
    }

    startSpinner(text) {
        this.setState({ showSpinner: true, spinnerState: 'sync', spinnerText: text });
    }

    stopSpinner(message, state = 'success') {
        this.setState({ showSpinner: true, spinnerState: state, spinnerText: message });
        setTimeout(() => {
            this.setState({ showSpinner: false, spinnerState: 'sync', spinnerText: '' });
        }, 3000);
    }

    handleSearchChange(list, value) {
        this.setState({ [list]: { ...this.state[list], searchTerm: value } });

        if (this.searchTimeouts[list]) clearTimeout(this.searchTimeouts[list]);
        this.searchTimeouts[list] = setTimeout(() => {
            this.fetchList(list, { currentPage: 1, searchTerm: value });
        }, 400);
    }

    async handleBlockSubmit(event) {
        event.preventDefault();
        this.startSpinner(__('Blocking number...'));

        try {
            const response = await fetch(this.endpoint(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken(),
                },
                body: JSON.stringify({
                    phone: this.state.blockPhone,
                    note: this.state.blockNote || null,
                }),
            });

            if (response.status === 422) {
                const r = await response.json();
                this.stopSpinner(r.errors?.phone?.[0] || r.errors?.note?.[0] || __('Please enter a valid phone number.'), 'error');
                return;
            }
            if (!response.ok) {
                throw new Error(`Failed to block number (HTTP ${response.status})`);
            }

            const r = await response.json();
            const phone = displayPhone(r.data.phone);
            this.stopSpinner(response.status === 201
                ? `${phone} ${__('is now blocked')}`
                : `${phone} ${__('was already blocked')}`);
            this.setState({ blockPhone: '', blockNote: '' });
            await this.fetchList('blocked', { currentPage: 1 });
        } catch (error) {
            console.error(error);
            this.stopSpinner(__('Error blocking number...'), 'error');
        }
    }

    async handleRemove(optOut) {
        if (!window.confirm(`${__('Remove')} ${displayPhone(optOut.phone)} ${__('from the blocked numbers?')}`)) {
            return;
        }
        this.startSpinner(__('Removing...'));

        try {
            const response = await fetch(`${this.endpoint()}/${optOut.id}`, {
                method: 'DELETE',
                headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrfToken() },
            });
            if (!response.ok) {
                throw new Error(`Failed to remove blocked number (HTTP ${response.status})`);
            }

            this.stopSpinner(__('Number removed from the blocked list'));
            await this.fetchList('blocked');
        } catch (error) {
            console.error(error);
            this.stopSpinner(__('Error removing number...'), 'error');
        }
    }

    renderSearch(list) {
        return (
            <div style={{ maxWidth: 240 }}>
                <input
                    type="tel"
                    placeholder={__('Search by phone...')}
                    className="form-control form-control-sm"
                    maxLength={14}
                    value={formatPhoneUS(this.state[list].searchTerm || '')}
                    onChange={(e) => this.handleSearchChange(list, e.target.value)}
                    onKeyDown={allowDigitsOnly}
                />
            </div>
        );
    }

    renderTableBody(list, emptyText, noMatchText, renderTable) {
        const { items, loading, searchTerm } = this.state[list];

        return (
            <div className="card-body p-0" style={{ position: 'relative', minHeight: 100 }}>
                {loading && (
                    <div className="d-flex justify-content-center align-items-center py-4"
                         style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="sr-only">{__('Loading...')}</span>
                        </div>
                    </div>
                )}

                {!loading && items.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        {searchTerm ? noMatchText : emptyText}
                    </div>
                ) : (
                    <div className="table-responsive">{renderTable(items)}</div>
                )}
            </div>
        );
    }

    renderPagination(list) {
        const { currentPage, totalPages, totalItems, itemsPerPage } = this.state[list];
        const from = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
        const to = Math.min(currentPage * itemsPerPage, totalItems);
        const goTo = (page) => this.fetchList(list, { currentPage: page });

        return (
            <div className="card-footer d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <span className="text-muted small mr-3">
                        {totalItems > 0
                            ? `${__('Showing')} ${from}-${to} ${__('of')} ${totalItems}`
                            : `0 ${__('results')}`}
                    </span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => this.fetchList(list, { itemsPerPage: parseInt(e.target.value), currentPage: 1 })}
                        className="form-control form-control-sm d-inline-block"
                        style={{ width: 70 }}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    <span className="text-muted small ml-2">{__('per page')}</span>
                </div>

                {totalPages > 1 && (
                    <nav>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => goTo(currentPage - 1)}
                                        disabled={currentPage === 1}>
                                    &lsaquo;
                                </button>
                            </li>

                            {buildPages(currentPage, totalPages, 2, 1).map((p, idx) =>
                                p === '…' ? (
                                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                                        <span className="page-link">…</span>
                                    </li>
                                ) : (
                                    <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => goTo(p)}>
                                            {p}
                                        </button>
                                    </li>
                                )
                            )}

                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => goTo(currentPage + 1)}
                                        disabled={currentPage === totalPages}>
                                    &rsaquo;
                                </button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>
        );
    }

    render() {
        const { businessGroupName, blockPhone, blockNote } = this.state;

        return (
            <div className="edit-vin w-100 right-side-content unsubscribe-page">
                <div className="w-100 border-bottom d-flex align-items-center bg-white p-2 py-3 shadow-sm">
                    <div className="d-flex ml-4 mr-auto">
                        <a className="btn btn-outline-primary text-nowrap mr-2"
                           href={window.location.pathname.replace(/\/unsubscribe$/, '/manual')}>{__('Back to Send Rating Request')}
                        </a>
                    </div>
                </div>

                <div className="w-100 p-4">
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5 className="card-title mb-1">{__('Block a Phone Number')}</h5>
                            <p className="text-muted small mb-3">
                                {__('A blocked number receives no text messages from us, whether the customer is currently subscribed, already unsubscribed, or not in our system yet. Replies in a conversation the customer starts are still delivered.')}
                                {businessGroupName && (
                                    <> {__('Applies to all locations in')} <strong>{businessGroupName}</strong>.</>
                                )}
                            </p>
                            <form className="d-flex align-items-end flex-wrap" onSubmit={(e) => this.handleBlockSubmit(e)}>
                                <div className="mr-3 mb-2" style={{ maxWidth: 280 }}>
                                    <label htmlFor="block-phone" className="small text-muted mb-1">
                                        {__('Phone Number')}
                                    </label>
                                    <input
                                        id="block-phone"
                                        type="tel"
                                        className="form-control"
                                        placeholder="(555) 555-5555"
                                        value={formatPhoneUS(blockPhone)}
                                        onChange={(e) => this.setState({ blockPhone: usPhoneDigits(e.target.value) })}
                                        onKeyDown={allowDigitsOnly}
                                    />
                                </div>
                                <div className="mr-3 mb-2 flex-grow-1" style={{ maxWidth: 360 }}>
                                    <label htmlFor="block-note" className="small text-muted mb-1">
                                        {__('Note (optional)')}
                                    </label>
                                    <input
                                        id="block-note"
                                        type="text"
                                        className="form-control"
                                        maxLength={255}
                                        value={blockNote}
                                        onChange={(e) => this.setState({ blockNote: e.target.value })}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-primary mb-2"
                                    disabled={blockPhone.replace(/\D/g, '').length < 10}
                                >
                                    {__('Block Number')}
                                </button>
                            </form>
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="mb-0">{__('Blocked Numbers')}</h5>
                            {this.renderSearch('blocked')}
                        </div>
                        {this.renderTableBody('blocked', __('No blocked numbers yet.'), __('No numbers found matching your search.'), (items) => (
                            <table className="table table-hover mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        <th>{__('Phone')}</th>
                                        <th>{__('Note')}</th>
                                        <th>{__('Blocked')}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((optOut) => (
                                        <tr key={optOut.id}>
                                            <td>{displayPhone(optOut.phone)}</td>
                                            <td>{optOut.note || '-'}</td>
                                            <td>{formatDate(optOut.created_at)}</td>
                                            <td className="text-right">
                                                <button className="btn btn-sm btn-outline-danger"
                                                        onClick={() => this.handleRemove(optOut)}>
                                                    {__('Remove')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ))}
                        {this.renderPagination('blocked')}
                    </div>
                </div>

                {this.state.showSpinner &&
                    <Spinner spinnerState={this.state.spinnerState} spinnerText={this.state.spinnerText}/>
                }
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        state: state,
        business: state.business
    }
}

export default connect(mapStateToProps)(AutoPilotUnsubscribe)
